import i18next from 'i18next'
import get from 'lodash/get'
import { GridColDef } from '@microservices/wiskey-react-table-component'
import { ColumnDef } from '@tanstack/react-table'

import { getParamsString } from '@helpers'
import { FIELD_VALUE_TYPE } from '@constants'

import {
  ConfigField,
  GANTT_BIND_TYPE_LIST,
  GanttActionType,
  GanttColumnForm,
  GanttFormCommands,
  SectionType,
} from '@gantt/components/GanttCreateOrEdit/types'

import { ResourceColumnCell } from '../components/GanttCreateOrEdit/components/ResourceColumnCell'

const accessorFnActionValue = (originalRow: GanttActionType) => {
  const bindType = originalRow.actionField?.bindType
  const value = getParamsString(originalRow.actionField)

  switch (bindType) {
    case GANTT_BIND_TYPE_LIST.JSON:
      return JSON.stringify(value)
    case GANTT_BIND_TYPE_LIST.JS:
      return value
    case GANTT_BIND_TYPE_LIST.COMMANDS:
      return (value as GanttFormCommands).map(item => item.name)
    case GANTT_BIND_TYPE_LIST.FORM:
    case GANTT_BIND_TYPE_LIST.TOOLTIP:
    case GANTT_BIND_TYPE_LIST.CONTEXT_MENU:
      return value?.label

    default:
      return value
  }
}

const accessorFnSegmentValue = (originalRow: SectionType) => {
  const bindType = originalRow?.title?.bindType
  const value = get(originalRow, ['title', bindType || GANTT_BIND_TYPE_LIST.STATIC])

  switch (bindType) {
    case GANTT_BIND_TYPE_LIST.FIELD_ARRAY:
      return (value as ConfigField[]).map(item => item.pathStr).join(', ')
    default:
      return value?.pathStr ?? value
  }
}

const accessorFnFieldValue = (originalRow: GanttColumnForm) => {
  const fields = originalRow.fields

  return fields.map(item => {
    switch (item.bindType) {
      case GANTT_BIND_TYPE_LIST.STATIC:
        return item.static
      case GANTT_BIND_TYPE_LIST.FIELD:
        return item.field?.pathStr
      case GANTT_BIND_TYPE_LIST.JS:
        return item.js
    }
  })
}

const accessorFnBindType = (originalRow: GanttColumnForm) => {
  const fields = originalRow.fields

  return fields.map(item => item.bindType)
}

const accessorFnFieldValueType = (originalRow: GanttColumnForm) => {
  const fields = originalRow.fields

  return fields.map(item => {
    if (item.bindType === GANTT_BIND_TYPE_LIST.FIELD) {
      return item.field?.pathArray[item.field?.pathArray.length - 1].valueType
    }

    return FIELD_VALUE_TYPE.STRING
  })
}

const accessorFnTargetValue = (originalRow: GanttActionType) => {
  const target = originalRow.target

  if (!target) return ''

  if (Array.isArray(target)) {
    return target
      .map(item => {
        if (typeof item === 'object' && item !== null && 'label' in item) {
          return item.label
        }

        return item
      })
      .join(', ')
  }

  if (typeof target === 'object' && target !== null && 'label' in target) {
    return target.label
  }

  return target
}

export const GANTT_RESOURCE_COLUMN_ROW_HEIGHT = 60

export const GANTT_CONFIG_COLUMNS: ColumnDef<GanttColumnForm>[] = [
  {
    accessorKey: 'code',
    header: i18next.t('ganttCreate.columnForm.code'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'title',
    header: i18next.t('ganttCreate.columnForm.title'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'bindType',
    header: i18next.t('ganttCreate.columnForm.bindType'),
    meta: { flex: 1 },
    accessorFn: accessorFnBindType,
    cell: ({ getValue }) => {
      const values = getValue() as string[]

      return <ResourceColumnCell values={values} />
    },
  },
  {
    accessorKey: 'value',
    header: i18next.t('ganttCreate.columnForm.value'),
    meta: { flex: 1 },
    accessorFn: accessorFnFieldValue,
    cell: ({ getValue }) => {
      const values = getValue() as string[]

      return <ResourceColumnCell values={values} />
    },
  },
  {
    accessorKey: 'valueType',
    header: i18next.t('ganttCreate.columnForm.valueType'),
    meta: { flex: 1 },
    accessorFn: accessorFnFieldValueType,
    cell: ({ getValue }) => {
      const values = getValue() as string[]

      return <ResourceColumnCell values={values} />
    },
  },
  {
    accessorKey: 'pinnedColumn',
    header: i18next.t('ganttCreate.columnForm.pinnedColumn'),
    meta: { flex: 1 },
  },
]

export const GANTT_CONFIG_ACTION_COLUMNS = [
  {
    accessorKey: 'code',
    header: i18next.t('ganttCreate.actionForm.code'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'title',
    header: i18next.t('ganttCreate.actionForm.title'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'eventCode',
    header: i18next.t('ganttCreate.actionForm.eventCode'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'actionCode',
    header: i18next.t('ganttCreate.actionForm.actionCode'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'target',
    header: i18next.t('ganttCreate.actionForm.columnTarget'),
    meta: { flex: 1 },
    accessorFn: accessorFnTargetValue,
  },
  {
    accessorKey: 'actionField.bindType',
    header: i18next.t('ganttCreate.actionForm.valueType'),
    meta: { flex: 1 },
  },
  {
    header: i18next.t('ganttCreate.actionForm.value'),
    meta: { flex: 1 },
    accessorFn: accessorFnActionValue,
  },
]

export const GANTT_CONFIG_ACTION_TIMELINE_COLUMNS = [
  {
    accessorKey: 'code',
    header: i18next.t('ganttCreate.actionForm.code'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'title',
    header: i18next.t('ganttCreate.actionForm.title'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'eventCode',
    header: i18next.t('ganttCreate.actionForm.eventCode'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'actionCode',
    header: i18next.t('ganttCreate.actionForm.actionCode'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'target',
    header: i18next.t('ganttCreate.actionForm.segmentTarget'),
    meta: { flex: 1 },
    accessorFn: accessorFnTargetValue,
  },
  {
    accessorKey: 'actionField.bindType',
    header: i18next.t('ganttCreate.actionForm.valueType'),
    meta: { flex: 1 },
  },
  {
    header: i18next.t('ganttCreate.actionForm.value'),
    meta: { flex: 1 },
    accessorFn: accessorFnActionValue,
  },
]

export const GANTT_SEGMENT_COLUMNS = [
  {
    accessorKey: 'link.systemName',
    header: i18next.t('ganttCreate.timelineForm.systemName'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'link.axisX.field.pathStr',
    header: i18next.t('ganttCreate.objectLinks'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'datetimeStart.field.pathStr',
    header: i18next.t('ganttCreate.timelineForm.dateTimeStart'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'datetimeEnd.field.pathStr',
    header: i18next.t('ganttCreate.timelineForm.dateTimeEnd'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'duration.field.pathStr',
    header: i18next.t('ganttCreate.timelineForm.duration'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'title.static.pathStr',
    header: i18next.t('ganttCreate.timelineForm.title'),
    meta: { flex: 1 },
    accessorFn: accessorFnSegmentValue,
  },
]

export const GANTT_LIST_COLUMNS: GridColDef[] = [
  {
    field: 'id',
    headerName: i18next.t('configGantt.ganttList.id'),
  },
  {
    field: 'title',
    headerName: i18next.t('configGantt.ganttList.title'),
    flex: 1,
  },
  {
    field: 'resourceObjectTitle',
    headerName: i18next.t('configGantt.ganttList.resourceObjectCode'),
    flex: 1,
  },
  {
    field: 'timelineObjects',
    headerName: i18next.t('configGantt.ganttList.timelineObjectCode'),
    flex: 1,
    valueFormatter: originalValue => {
      const value: Record<string, string> = originalValue

      return Object.values(value).join(', ')
    },
  },
]
